import React, { useCallback } from 'react';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import AspectRatioIcon from '@mui/icons-material/AspectRatio';
import InfoIcon from '@mui/icons-material/Info';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import List from '@mui/material/List';
import Divider from '@mui/material/Divider';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import useNotification from '../../_hook/useNotification';
import { IAttachmentSubSchemaPartial } from '../../../../graphql/type/subSchema/attachment';
import BaseAttachmentUploadItem from './BaseAttachmentUploadItem';

interface IProps {
  uploadingCount?: number;
  onUpload?: (fileList: FileList | null) => void;
  onDelete?: (filename: string) => void;
  onPublish?: (filenames: string[]) => void;
  onUnpublish?: (filenames: string[]) => void;
  attachments: IAttachmentSubSchemaPartial &
    {
      status: boolean;
      filename: string;
    }[];
  uploadInfo?: {
    ratio?: string;
    size?: string;
    acceptedUploadFileTypes?: string;
  };
  maximumUploadSize?: number;
  color?: 'primary';
}

const useStyles = makeStyles<Theme, IProps>((theme: Theme) => ({
  infoPaper: {
    padding: theme.spacing(1),
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  root: {
    width: '100%',
    backgroundColor: theme.palette.background.paper,
  },
  dropzone: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    cursor: 'pointer',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '20px',
    borderWidth: '2px',
    borderRadius: '2px',
    borderColor: ({ color }) =>
      color === 'primary' ? theme.palette.primary.main : '#eeeeee',
    borderStyle: 'dashed',
    backgroundColor: theme.palette.mode === 'light' ? '#fafafa' : '#000',
    color: ({ color }) =>
      color === 'primary' ? theme.palette.primary.main : '#bdbdbd',
    outline: 'none',
    transition: 'border .24s ease-in-out',
  },
}));

export default function BaseAttachmentUpload(props: IProps) {
  const {
    onDelete,
    onPublish,
    onUnpublish,
    attachments,
    uploadingCount,
    uploadInfo,
    maximumUploadSize,
    onUpload,
  } = props;
  const { t } = useTranslation();
  const { notification } = useNotification();
  const classes = useStyles(props);
  const onDrop = useCallback((acceptedFiles) => {
    if (onUpload) {
      onUpload(acceptedFiles);
    }
  }, []);
  const options = {
    accept:
      uploadInfo && uploadInfo.acceptedUploadFileTypes
        ? uploadInfo.acceptedUploadFileTypes
        : undefined,
    validator: (file: File) => {
      const { size } = file;
      if (maximumUploadSize && file && size > maximumUploadSize) {
        notification.error(
          t('__fileName__is_over__size__limit', {
            fileName: file.name,
            size:
              uploadInfo && uploadInfo.size
                ? uploadInfo.size
                : maximumUploadSize,
          })
        );

        return {
          code: 'size-too-large',
          message: `Size is larger than ${maximumUploadSize}`,
        };
      }

      return null;
    },
    onDrop,
  };
  const {
    // acceptedFiles,
    // fileRejections,
    getRootProps,
    getInputProps,
  } = useDropzone(options);

  return (
    <>
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <div {...getRootProps({ className: classes.dropzone })}>
        {/* eslint-disable-next-line react/jsx-props-no-spreading */}
        <input {...getInputProps()} />
        <Grid container justifyContent="center" spacing={1}>
          <Grid item xs={12} container justifyContent="center">
            <Typography variant="body1">
              {t('drop_file_here_or_click_to_browse_files')}
            </Typography>
          </Grid>
          {uploadInfo && (
            <>
              {uploadInfo.acceptedUploadFileTypes && (
                <Grid item xs={12} container justifyContent="center">
                  <Typography variant="body1">
                    ({t('only')} {uploadInfo.acceptedUploadFileTypes})
                  </Typography>
                </Grid>
              )}
              {uploadInfo.ratio && (
                <Grid
                  item
                  xs={6}
                  container
                  alignItems="center"
                  justifyContent="flex-end"
                >
                  <AspectRatioIcon />
                  <Typography variant="caption">
                    &nbsp;{uploadInfo.ratio}
                  </Typography>
                </Grid>
              )}
              {uploadInfo.size && (
                <Grid
                  item
                  xs={6}
                  container
                  alignItems="center"
                  justifyContent="flex-start"
                >
                  <InfoIcon />
                  <Typography variant="caption">
                    &nbsp;{uploadInfo.size}
                  </Typography>
                </Grid>
              )}
            </>
          )}
        </Grid>
      </div>
      <Grid container spacing={1}>
        <List className={classes.root}>
          <Divider component="li" />
          {new Array(uploadingCount).fill(0).map((_, index) => (
            <BaseAttachmentUploadItem
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              loading
              defaultLoadingHeight={attachments.length ? undefined : '36px'}
            />
          ))}
          {attachments.map((attachment) => (
            <BaseAttachmentUploadItem
              key={attachment.filename}
              onDelete={onDelete}
              onPublish={onPublish}
              onUnpublish={onUnpublish}
              attachment={attachment}
            />
          ))}
        </List>
      </Grid>
    </>
  );
}
